import { MenuItem, isMeetingRoomExtras } from 'types/menuTypes';

export interface MenuPath {
  items: MenuItem[];
  level: 'first' | 'second' | 'third';
  documentId: string;
  path: string;
}

/**
 * Encontra o caminho completo de um menu por path
 * Retorna o caminho hierárquico completo (pai -> filho -> neto)
 */
export const findMenuPathByUrl = (menu: MenuItem[], targetPath: string): MenuPath | null => {
  if (!menu || menu.length === 0) {
    console.log('[findMenuPathByUrl] Menu vazio, aguardando carregamento');
    return null;
  }

  console.log(
    '[findMenuPathByUrl] Procurando path:',
    targetPath,
    'em menu com',
    menu.length,
    'itens'
  );

  const findInMenu = (
    items: MenuItem[],
    path: string,
    parentPath: MenuItem[] = []
  ): MenuItem[] | null => {
    for (const item of items) {
      const currentPath = [...parentPath, item];

      if (item.path === path) {
        console.log('[findMenuPathByUrl] Menu encontrado:', item.label);
        return currentPath;
      }

      if (item.subMenu?.length) {
        const found = findInMenu(item.subMenu, path, currentPath);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  const menuPath = findInMenu(menu, targetPath);
  if (!menuPath?.length) {
    console.log('[findMenuPathByUrl] Menu não encontrado para path:', targetPath);
    return null;
  }

  const lastItem = menuPath[menuPath.length - 1];
  const level = menuPath.length === 1 ? 'first' : menuPath.length === 2 ? 'second' : 'third';

  // Extrai documentId
  let documentId = lastItem.feature?.documentId || '';
  if (!documentId && lastItem.extras && isMeetingRoomExtras(lastItem.extras)) {
    documentId = lastItem.extras.internal_id || '';
  }

  console.log('[findMenuPathByUrl] Menu encontrado com sucesso:', {
    path: lastItem.path,
    label: lastItem.label,
    level,
    documentId,
  });

  return {
    items: menuPath,
    level,
    documentId,
    path: lastItem.path,
  };
};

/**
 * Encontra o caminho completo de um menu por documentId
 */
export const findMenuPathByDocumentId = (
  menu: MenuItem[],
  targetId: string,
  targetPath?: string
): MenuPath | null => {
  const findInMenu = (
    items: MenuItem[],
    id: string,
    path?: string,
    parentPath: MenuItem[] = []
  ): MenuItem[] | null => {
    for (const item of items) {
      const currentPath = [...parentPath, item];

      // Se temos um targetPath específico, prioriza a correspondência exata
      if (path) {
        if (item.path === path) {
          const hasMatchingDocumentId =
            item.feature?.documentId === id ||
            (item.extras && isMeetingRoomExtras(item.extras) && item.extras.internal_id === id);

          if (hasMatchingDocumentId) {
            return currentPath;
          }
        }
      } else {
        // Se não temos targetPath, usa a lógica original
        if (
          item.feature?.documentId === id ||
          (item.extras && isMeetingRoomExtras(item.extras) && item.extras.internal_id === id)
        ) {
          return currentPath;
        }
      }

      // Verifica submenus recursivamente
      if (item.subMenu?.length) {
        const found = findInMenu(item.subMenu, id, path, currentPath);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  // Se temos um path específico, tenta encontrar primeiro por path
  if (targetPath) {
    const menuPathByUrl = findMenuPathByUrl(menu, targetPath);
    if (menuPathByUrl) {
      const lastItem = menuPathByUrl.items[menuPathByUrl.items.length - 1];
      const hasMatchingDocumentId =
        lastItem.feature?.documentId === targetId ||
        (lastItem.extras &&
          isMeetingRoomExtras(lastItem.extras) &&
          lastItem.extras.internal_id === targetId);

      if (hasMatchingDocumentId) {
        console.log('[menuUtils] Menu encontrado por path + documentId:', targetPath);
        return menuPathByUrl;
      }
    }
  }

  const menuPath = findInMenu(menu, targetId, targetPath);
  if (!menuPath?.length) return null;

  const lastItem = menuPath[menuPath.length - 1];
  const level = menuPath.length === 1 ? 'first' : menuPath.length === 2 ? 'second' : 'third';

  // Extrai documentId
  let documentId = lastItem.feature?.documentId || '';
  if (!documentId && lastItem.extras && isMeetingRoomExtras(lastItem.extras)) {
    documentId = lastItem.extras.internal_id || '';
  }

  return {
    items: menuPath,
    level,
    documentId,
    path: lastItem.path,
  };
};

/**
 * Encontra o primeiro menu válido (com documentId ou internal_id)
 */
export const findFirstValidMenu = (menu: MenuItem[]): MenuItem | null => {
  for (const item of menu) {
    // Verifica se tem documentId no feature
    if (item.feature?.documentId) {
      return item;
    }
    // Verifica se é um menu de reunião e tem internal_id
    if (item.extras && isMeetingRoomExtras(item.extras) && item.extras.internal_id) {
      return item;
    }
    // Verifica submenus
    if (item.subMenu?.length) {
      const found = findFirstValidMenu(item.subMenu);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Encontra um menu de fallback inteligente baseado no contexto atual
 */
export const findSmartFallback = (
  menu: MenuItem[],
  currentPath: string,
  currentLevel: 'first' | 'second' | 'third' | null
): MenuPath | null => {
  console.log('[menuUtils] Procurando fallback inteligente para:', currentPath);

  // 1. Tenta encontrar menu similar ao atual
  const currentMenuPath = findMenuPathByUrl(menu, currentPath);
  if (currentMenuPath?.items.length) {
    const parentMenu = currentMenuPath.items[0];
    if (parentMenu.subMenu?.length) {
      const firstSubMenu = parentMenu.subMenu[0];
      const subMenuPath = findMenuPathByDocumentId(
        menu,
        firstSubMenu.feature?.documentId ||
          (firstSubMenu.extras && isMeetingRoomExtras(firstSubMenu.extras)
            ? firstSubMenu.extras.internal_id
            : '') ||
          '',
        firstSubMenu.path
      );
      if (subMenuPath) {
        console.log('[menuUtils] Fallback 1: Submenu do menu atual:', subMenuPath.path);
        return subMenuPath;
      }
    }
  }

  // 2. Tenta encontrar menu ativo no mesmo nível
  if (currentLevel) {
    const currentLevelNumber = currentLevel === 'first' ? 1 : currentLevel === 'second' ? 2 : 3;
    const menusAtSameLevel = menu.filter(item => {
      if (currentLevelNumber === 1) return item.level === 'first' && item.isActive;
      if (currentLevelNumber === 2) return item.level === 'second' && item.isActive;
      return item.level === 'third' && item.isActive;
    });

    if (menusAtSameLevel.length > 0) {
      const firstMenu = menusAtSameLevel[0];
      const menuPath = findMenuPathByDocumentId(
        menu,
        firstMenu.feature?.documentId ||
          (firstMenu.extras && isMeetingRoomExtras(firstMenu.extras)
            ? firstMenu.extras.internal_id
            : '') ||
          ''
      );
      if (menuPath) {
        console.log('[menuUtils] Fallback 2: Menu no mesmo nível:', menuPath.path);
        return menuPath;
      }
    }
  }

  // 3. Último recurso: primeiro menu ativo
  const firstActiveMenu = menu.find(item => item.isActive);
  if (firstActiveMenu?.subMenu?.length) {
    const firstSubMenu = firstActiveMenu.subMenu[0];
    const menuPath = findMenuPathByDocumentId(
      menu,
      firstSubMenu.feature?.documentId ||
        (firstSubMenu.extras && isMeetingRoomExtras(firstSubMenu.extras)
          ? firstSubMenu.extras.internal_id
          : '') ||
        ''
    );
    if (menuPath) {
      console.log('[menuUtils] Fallback 3: Primeiro menu ativo:', menuPath.path);
      return menuPath;
    }
  }

  console.warn('[menuUtils] Nenhum fallback encontrado');
  return null;
};

/**
 * Valida se uma URL é válida baseada no menu atual
 */
export const isValidMenuUrl = (menu: MenuItem[], path: string): boolean => {
  return findMenuPathByUrl(menu, path) !== null;
};

/**
 * Valida se uma rota é válida baseada no menu de forma recursiva
 * Mais robusta que isValidMenuUrl para validações de inicialização
 */
export const isValidMenuRoute = (menu: MenuItem[], path: string): boolean => {
  const checkMenu = (menuItem: MenuItem): boolean => {
    if (menuItem.path === path) return true;
    if (menuItem.subMenu) {
      return menuItem.subMenu.some(checkMenu);
    }
    return false;
  };

  return menu.some(checkMenu);
};

/**
 * Extrai documentId de um MenuItem
 */
export const extractDocumentId = (menuItem: MenuItem): string | null => {
  // Primeiro tenta pegar o documentId do feature
  let documentId = menuItem.feature?.documentId || null;

  // Se não tiver, tenta pegar do internal_id para menus de reunião
  if (!documentId && menuItem.extras) {
    const extras = menuItem.extras;
    if (isMeetingRoomExtras(extras)) {
      documentId = extras.internal_id || null;
    }
  }

  return documentId;
};
